import { collection, getDocs, query, where, orderBy } from "firebase/firestore";
import { db } from "./firebase";

/**
 * Month abbreviations for the number format.
 */
const MONTHS = ["JAN","FEB","MAR","APR","MAY","JUN","JUL","AUG","SEP","OCT","NOV","DEC"];

/**
 * Returns the current year and month tag, e.g. "2026MAR"
 */
const getYearMonthTag = () => {
  const now = new Date();
  return `${now.getFullYear()}${MONTHS[now.getMonth()]}`;
};

/**
 * Fetches all documents from a Firestore collection whose document number
 * matches the current year+month prefix, finds the MAX serial, and returns
 * the next serial number as a zero-padded 3-digit string.
 *
 * Logic (per user spec):
 *   - Next = max existing serial for this month + 1
 *   - Deleting the highest serial → that number becomes available again
 *   - Deleting a lower serial → max stays, next number stays higher
 *
 * @param {string} prefix        - e.g. "INV", "QUO", "SER"
 * @param {string} collectionName - Firestore collection: "invoices", "quotations", "service_bills"
 * @param {string} fieldName     - The document number field: "invoiceNumber", "quotationNumber", "billNumber"
 * @returns {Promise<string>}    - e.g. "INV2026MAR003"
 */
export const getNextDocumentNumber = async (prefix, collectionName, fieldName) => {
  const tag = getYearMonthTag();
  const fullPrefix = `${prefix}${tag}`;

  try {
    const snap = await getDocs(collection(db, collectionName));

    let maxSerial = 0;

    snap.forEach((doc) => {
      const num = doc.data()[fieldName];
      if (num && num.startsWith(fullPrefix)) {
        const serialStr = num.slice(fullPrefix.length); // last 3 chars
        const serial = parseInt(serialStr, 10);
        if (!isNaN(serial) && serial > maxSerial) {
          maxSerial = serial;
        }
      }
    });

    const nextSerial = maxSerial + 1;
    return `${fullPrefix}${String(nextSerial).padStart(3, "0")}`;
  } catch (err) {
    // Fallback to timestamp-based if Firestore query fails
    console.warn("getNextDocumentNumber fallback:", err.message);
    const rand = String(Math.floor(Math.random() * 999) + 1).padStart(3, "0");
    return `${fullPrefix}${rand}`;
  }
};
